import React, { type ReactElement, type ReactNode } from 'react';
import { faChartBar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import type { Column } from 'react-table';
import ProgressBar from 'feature-components/report-parts/progress-bar/progress-bar';
import { QuestionIdentifierSVG } from 'feature-components/report-parts/question-identifier/question-identifier';
import type { Statistics } from 'feature-components/reports/item-assessment-report';
import {
  getCategoryLabel,
  getCategoryTypeLabel
} from 'feature-components/reports/utils/getCategoryLabel';
import type { Category } from 'services/assist-api/entities/test-item/test-items';
import { TestType } from 'services/assist-api/entities/test/tests';

export const getCategory = (categories: Category[]): Category | undefined =>
  categories.find((c) => !c.children || !c.children.length);

type QuestionColProps = {
  canHaveStyledQuestionType?: boolean;
  getText?: (question: Statistics) => ReactNode;
  testType: TestType | undefined;
};

export const QuestionCol = ({
  canHaveStyledQuestionType,
  getText,
  testType
}: QuestionColProps): Column<Statistics> => ({
  Header: testType === TestType.Standard ? 'Question' : 'Report Link',
  accessor: 'identifier',
  Cell: ({ value, row: { original: question } }): ReactElement => {
    if (testType === TestType.Standard) {
      return (
        <div className="d-flex">
          <Link to={question.testItemLink}>
            <QuestionIdentifierSVG
              question={{
                ...question,
                identifier: String(value)
              }}
              onClickQuestion={(): null => null} // using a link instead
              radius={14}
              fontSize={16}
              canHaveStyledQuestionType={canHaveStyledQuestionType}
            />
          </Link>
          <div className="ms-2">{getText ? getText(question) : question.questionType?.value}</div>
        </div>
      );
    }

    // for adaptive or aggregate reports (where we don't have a test type)
    return (
      <Link to={question.testItemLink}>
        <FontAwesomeIcon icon={faChartBar} className="mx-2" /> Report
      </Link>
    );
  },
  className: 'text-capitalize',
  width: 90
});

export const CategoryCol = (areaIdentifier: string): Column<Statistics> => ({
  Header: getCategoryLabel(areaIdentifier),
  id: 'categories',
  accessor: ({ categories }): string => {
    const category = getCategory(categories);
    if (!category) return '';
    if (category.alternativeName) return `${category.alternativeName} / ${category.name}`;
    return category.name;
  },
  disableSortBy: true,
  width: 150
});

export const StimulusTypeCol = (
  stimulusTypeAccessor: string,
  areaIdentifier: string
): Column<Statistics> => ({
  Header: getCategoryTypeLabel(areaIdentifier),
  id: 'stimulus-type',
  accessor: (data) =>
    getCategory(data.categories)?.metadatas?.find(
      (meta) => meta.name.toLowerCase() === stimulusTypeAccessor
    )?.value,
  className: 'text-capitalize',
  width: 120
});

export const QuestionTypeCol: Column<Statistics> = {
  Header: 'Question Type',
  id: 'question-type',
  accessor: (data) =>
    data.metadatas?.find((meta) => meta.name.toLowerCase() === 'question type')?.value,
  sortType: 'string',
  className: 'text-capitalize',
  width: 100
};

export const QuestionDescriptionCol: Column<Statistics> = {
  Header: 'Question Description',
  accessor: 'description',
  disableSortBy: true,
  Cell: ({ value }) => <div dangerouslySetInnerHTML={{ __html: value }} />,
  width: 260
};

export const ScaleDifficultyCol: Column<Statistics> = {
  Header: 'Scale Score',
  accessor: 'scaleScore',
  Cell: ({ value }) => <>{value.toFixed(1.0)}</>,
  className: 'text-end',
  width: 60
};

export const PercentageCorrectCol = (showReferenceData: boolean): Column<Statistics> => ({
  Header: 'Correct (%)',
  id: 'percent-correct',
  accessor: ({ numResponses, numResponsesCorrect, percentageCorrect, percentageNational }) => ({
    numResponses,
    numResponsesCorrect,
    percentageCorrect,
    percentageNational
  }),
  sortType: (a, b, columnId) =>
    a.values[columnId].percentageCorrect - b.values[columnId].percentageCorrect,
  Cell: ({ value }) => (
    <div key="progress-value">
      <span>
        {value.percentageCorrect}% ({value.numResponsesCorrect}/{value.numResponses})
      </span>
      <ProgressBar
        bar={value.percentageCorrect}
        triangle={value.percentageNational}
        showTriangle={showReferenceData}
      />
    </div>
  ),
  className: 'text-end',
  width: 100
});

export const NationalPercentageCol: Column<Statistics> = {
  Header: 'National Correct (%)',
  accessor: 'percentageNational',
  Cell: ({ value }) => <div>{value}%</div>,
  className: 'text-end',
  width: 80
};
